import React from "react";
import { Link } from "react-router-dom";
import PageShell, { SectionHeader } from "@/components/PageShell";
import NextRoom from "@/components/NextRoom";
import { CompassMark } from "@/components/CompassMark";
import { SPEAKING, PORTRAITS, TEXTURES, getObservation } from "@/data/content";

const SPEAK_INTENT = "/contact?intent=speaking";

export default function Speaking() {
  return (
    <PageShell
      testId="page-speaking"
      title="Speaking | Quentin Young"
      description="Speaking engagements with Quentin Young on human nature, culture, leadership, systems, brand, craft, achievement, and the pursuit of collective human potential."
      ogImage="/og-image.jpg"
      breadcrumbs={[{ name: "Home", url: "/" }, { name: "Speaking", url: "/speaking" }]}
    >
      {/* Hero over institutional space */}
      <section className="relative border-b border-ink-line overflow-hidden" data-testid="speaking-hero">
        <img
          src={TEXTURES.emptyRoom}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.14] grayscale"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(14,14,14,0.7) 0%, rgba(14,14,14,0.92) 100%)" }} />
        <div className="container-edge relative pt-20 md:pt-28 pb-16 md:pb-24">
          <p className="eyebrow-gold mb-6" data-testid="speaking-eyebrow">{SPEAKING.eyebrow}</p>
          <h1 className="display-serif text-4xl md:text-5xl lg:text-6xl text-paper-primary text-balance leading-[1.02] max-w-4xl">
            {SPEAKING.h1}
          </h1>
          <p className="mt-10 max-w-2xl font-serif text-xl md:text-2xl text-paper-secondary leading-snug text-pretty">
            {SPEAKING.opening}
          </p>
          <p className="mt-6 max-w-2xl text-paper-tertiary text-base md:text-lg leading-relaxed text-pretty">
            {SPEAKING.lead}
          </p>
          <div className="mt-12">
            <Link to={SPEAK_INTENT} data-testid="speaking-hero-cta" className="btn-quiet">
              <span>{SPEAKING.ctaLabel}</span>
              <span>→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Themes */}
      <section className="bg-ink-base py-16 md:py-24" data-testid="speaking-themes">
        <div className="container-edge">
          <SectionHeader eyebrow="Speaking themes" title="Where the talks come from." lead="Every theme grows from the same territory this site explores. Any can be shaped to a room." />
          <ul className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-ink-line border border-ink-line">
            {SPEAKING.themes.map((t) => {
              const obs = t.relatedObs ? getObservation(t.relatedObs) : null;
              return (
                <li key={t.number} data-testid={`speaking-theme-${t.number}`} className="bg-ink-base p-8 md:p-10">
                  <div className="flex items-baseline gap-4">
                    <span className="font-mono text-[11px] text-gold">{t.number}</span>
                    <h3 className="font-serif text-2xl text-paper-primary leading-tight text-balance">{t.title}</h3>
                  </div>
                  <p className="mt-5 text-paper-secondary text-base leading-relaxed text-pretty">{t.body}</p>
                  {obs && (
                    <Link
                      to={`/thinking/${obs.slug}`}
                      data-testid={`speaking-theme-related-${t.number}`}
                      className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gold/70 hover:text-gold-warm transition-colors duration-300"
                    >
                      <span>Related thinking: {obs.headline}</span>
                      <span>→</span>
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* Engagement types */}
      <section className="border-t border-ink-line bg-ink-base py-16 md:py-24">
        <div className="container-edge grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <SectionHeader eyebrow="Types of engagements" title="Formats that fit the room." />
          </div>
          <ul className="lg:col-span-8 flex flex-wrap gap-3 self-start" data-testid="speaking-engagements">
            {SPEAKING.engagementTypes.map((e, i) => (
              <li
                key={i}
                className="border border-ink-line px-5 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-paper-secondary hover:border-gold/50 hover:text-gold-warm transition-colors duration-300"
              >
                {e}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Who it is for / leave with */}
      <section className="border-t border-ink-line bg-ink-base py-16 md:py-24">
        <div className="container-edge grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-px md:bg-ink-line md:border md:border-ink-line">
          <div className="md:bg-ink-base md:p-10 lg:p-12" data-testid="speaking-who-for">
            <p className="eyebrow-gold mb-6">Who it is for</p>
            <ul className="space-y-4 text-paper-secondary text-base leading-relaxed">
              {SPEAKING.whoFor.map((w, i) => (
                <li key={i} className="flex gap-3"><span className="text-gold">·</span><span>{w}</span></li>
              ))}
            </ul>
          </div>
          <div className="md:bg-ink-base md:p-10 lg:p-12" data-testid="speaking-leave-with">
            <p className="eyebrow-gold mb-6">What audiences leave with</p>
            <ul className="space-y-4 text-paper-secondary text-base leading-relaxed">
              {SPEAKING.leaveWith.map((w, i) => (
                <li key={i} className="flex gap-3"><span className="text-gold">·</span><span>{w}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Media / bio / headshot */}
      <section className="border-t border-ink-line bg-ink-base py-16 md:py-24" data-testid="speaking-media">
        <div className="container-edge grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <div className="relative overflow-hidden border border-ink-line bg-ink-surface">
              <img
                src={PORTRAITS.threeQuarter}
                alt="Quentin Young"
                loading="lazy"
                className="w-full h-full object-cover aspect-[4/5] grayscale-[0.15]"
                data-testid="speaking-headshot"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="eyebrow mb-6">Media, bio, and assets</p>
            <p className="font-serif text-2xl md:text-3xl text-paper-primary leading-snug text-balance">
              {SPEAKING.bioShort}
            </p>
            <p className="mt-6 text-paper-tertiary text-base leading-relaxed max-w-xl text-pretty">
              A full press kit with high-resolution headshots, approved bio lengths, and past appearances will be added here as engagements are confirmed. For now, requests are handled directly.
            </p>
            <div className="mt-8 flex flex-wrap gap-6">
              <Link to="/media" data-testid="speaking-view-media" className="btn-arrow">
                <span>View media</span>
                <span className="arrow">→</span>
              </Link>
              <Link to={SPEAK_INTENT} data-testid="speaking-request-assets" className="btn-arrow">
                <span>Request bio and assets</span>
                <span className="arrow">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry CTA */}
      <section className="border-t border-ink-line bg-ink-surface/40 py-20 md:py-28">
        <div className="container-edge flex flex-col md:flex-row md:items-center md:justify-between gap-10">
          <div className="max-w-2xl">
            <CompassMark size={44} opacity={0.4} className="mb-8" alt="" />
            <h2 className="display-serif text-3xl md:text-5xl text-paper-primary text-balance leading-[1.05]">
              Bring Quentin into the room.
            </h2>
            <p className="mt-6 text-paper-secondary text-lg leading-relaxed max-w-xl text-pretty">
              Share the event, the audience, and what you want them to see differently. Fit matters more than format.
            </p>
          </div>
          <Link to={SPEAK_INTENT} data-testid="speaking-cta" className="btn-quiet shrink-0">
            <span>{SPEAKING.ctaLabel}</span>
            <span>→</span>
          </Link>
        </div>
      </section>

      <NextRoom
        eyebrow="Move to the next room"
        title="Read before the room."
        rooms={[
          { label: "Explore the thinking", to: "/thinking", hint: "the ideas" },
          { label: "Read the field notes", to: "/thinking", hint: "shorter observations" },
          { label: "View media", to: "/media", hint: "appearances" },
        ]}
        testId="speaking-next"
      />
    </PageShell>
  );
}
